import { fromUnixTime } from 'date-fns';
import { HeatMapTimeSeriesDataResponse, TimeSeriesData } from '@/core';
import { convertTimeSeriesDataToHeatMap } from '../heat-map-utils';
import { ResponseHandler, ResponseHandlerFn, ResultType } from '../types';
import {
  transformNativeHistogramToHeatmap,
  transformPromRangeQueryToTimeSeries,
  transformTimeSeriesHistogramToHeatmap,
} from './utils';

export const nativeHistogramResponseHandler: ResponseHandlerFn<HeatMapTimeSeriesDataResponse> = (
  result,
  metadata,
  notices,
  context
) => {
  if (result.length > 1) {
    notices.push({
      type: 'warning',
      message: 'Queries resulted in multiple series.',
      description: 'Consider aggregating the series.',
    });
  }

  const heatmap = transformNativeHistogramToHeatmap(result);
  return {
    type: 'HeatMap',
    data: {
      heatmap,
      metadata: {
        ...metadata,
        notices,
      },
      timeRange: { start: fromUnixTime(context.start), end: fromUnixTime(context.end) },
      stepMs: context.step * 1000,
    },
  };
};

export const timeSeriesHistogramResponseHandler: ResponseHandlerFn<HeatMapTimeSeriesDataResponse> = (
  result,
  metadata,
  notices,
  context
) => {
  const heatmap = transformTimeSeriesHistogramToHeatmap(result);
  return {
    type: 'HeatMap',
    data: {
      heatmap,
      metadata,
      timeRange: { start: fromUnixTime(context.start), end: fromUnixTime(context.end) },
      stepMs: context.step * 1000,
    },
  };
};

export const heatmapResponseHandler: ResponseHandler = {
  [ResultType.NativeHistogram]: nativeHistogramResponseHandler,

  [ResultType.TimeSeriesHistogram]: timeSeriesHistogramResponseHandler,

  [ResultType.TimeSeries]: (result, metadata, notices, context) => {
    const stepMs = context.step * 1000;
    const timeRange = { start: fromUnixTime(context.start), end: fromUnixTime(context.end) };
    const series = transformPromRangeQueryToTimeSeries({
      result,
      alias: context.alias ?? '',
      query: context.query,
      seriesName: context.series_name,
      seriesNameFormat: context.series_name_format,
    });
    const chartData: TimeSeriesData = {
      timeRange,
      stepMs,
      series,
      metadata,
    };

    const heatmap = convertTimeSeriesDataToHeatMap(chartData);
    return {
      type: 'HeatMap',
      data: {
        heatmap,
        timeRange,
        stepMs,
      },
    };
  },
};
